import { Injectable } from '@angular/core';
import {
  IEpRestCallOptions,
  EpApplicationService,
  EpErpRestService,
  EpDialogService
} from '@epicor/ux-platform';
import { ReplaySubject, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, share, take, tap } from 'rxjs/operators';
import { IContextData } from '@epicor/appcommunicator';
import { HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

import { AppLocalizationStrings } from '../app.localization.strings';

@Injectable({
  providedIn: 'root'
})
export class ContextService {
  STRINGS: any = AppLocalizationStrings;
  private _contextDataSubject = new ReplaySubject<SharedContextData>(1);
  private _headers: HttpHeaders = new HttpHeaders().append('CallSettings', '');

  private _callOptions: IEpRestCallOptions = {
    showProgress: false,
    headers: this._headers
  };

  get callOptions(): IEpRestCallOptions {
    return this._callOptions;
  }

  get headers(): HttpHeaders {
    return this._headers;
  }
  /**
   * Returns the latest context info, once it has been retrieved
   */
  contextData(): Observable<SharedContextData> {
    return this._contextDataSubject.asObservable().pipe(share());
  }

  validateLoggedIn(): boolean {
    if (!this.epApplicationService.tokenService.hasToken()) {
      this.router.navigate(['login']);
      return false;
    }
    return true;
  }

  readonly initialSetupComplete = new ReplaySubject<true>();

  constructor(
    private epApplicationService: EpApplicationService,
    private router: Router,
    private epErpRestService: EpErpRestService,
  ) {
  }

  public initializeAfterLogin(): Observable<any> {
    // Move your current constructor logic here
    return this.setSessionId().pipe(
      mergeMap(() => this.setUserSession()),
      tap(() => this.initialSetupComplete.next(true)),
      catchError(err => {
        EpDialogService.error(err);
        this.router.navigate(['login']);
        return of(null);
      })
    );
  }

  /**
   * Sets the session ID for the current user.
   */

  setSessionId(): Observable<string> {
    return this.epErpRestService.post('Ice.Lib.SessionModSvc', 'Login', {}).pipe(
      take(1),
      map((result: any) => (this.epErpRestService.currentSettings.sessionId = result.returnObj))
    );
  }

  /**
   * Sets the user session by retrieving current values from the server.
   */

  setUserSession(): Observable<any> {
    return this.epErpRestService.post('Ice.Lib.SessionModSvc', 'GetCurrentValuesFromServer', {}).pipe(
      take(1),
      map(result => {
        this.epErpRestService.currentSettings.company = result.parameters?.companyID;
        const contextData: SharedContextData = {
        company: result.parameters?.companyID,
        companyName: result.parameters?.companyName,
        sessionId: this.epErpRestService.currentSettings.sessionId
      };
      
      this._contextDataSubject.next(contextData);
        return result.parameters;
      })
    );
  }

  public static handleError(error: any) {
    let errorMessage: string;
    if (error.response) {
      if (error.response.error.ErrorMessage) {
        errorMessage = error.response.error.ErrorMessage;
      } else if (error.response.error) {
        errorMessage = error.response.error;
      } else {
        errorMessage = error.response.message;
      }
      EpDialogService.error(errorMessage);
    } else {
      EpDialogService.exception(AppLocalizationStrings.errorTitle, error);
    }
  }
}

export interface Company {
  Company1: string;
  Name: string;
  TenantID: string;
}

export interface SharedContextData extends IContextData {
  companyName?: string;
  mode?: string;
  isMultiTenant?: boolean;
  securityManager?: boolean;
  globalSecurityManager?: boolean;
  sessionId?: string;
  company?: string;
}
